import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to observe elements and trigger a callback when they become visible in the viewport.
 * @param {Object} options - Options for the hook.
 * @param {function} options.onVisible - Callback function to be called when an element becomes visible.
 * @param {number} [options.threshold=0.1] - Intersection threshold Set to 0.1.
 * @param {Array} [options.dependencies=[]] - Dependencies for the effect.
 * @returns {Object} - Object containing refs and observer.
 */

type Options = {
  onVisible: (index: number) => void;
  threshold?: number;
  dependencies?: any[];
};

export function useIntersectionFetch({ onVisible, dependencies = [], threshold = 0.1 }: Options) {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const seen = useRef<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const index = Number(entry.target.getAttribute('data-index'));
        if (entry.isIntersecting && !seen.current.has(index)) {
          seen.current.add(index);
          onVisible(index);
        }
      });
    },
    [onVisible]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      threshold,
    });
    observerRef.current = observer;

    refs.current.forEach((el) => {
      if (el) observer.unobserve(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleObserver, threshold, ...dependencies]);

  return { refs, observer: observerRef.current };
}
